import React, { useState, useEffect } from 'react';
import { 
  FileText, ShieldCheck, AlertTriangle, Edit3, 
  BookOpen, HelpCircle, ChevronRight, ChevronDown, 
  ChevronUp, ExternalLink, Link as LinkIcon, ArrowLeft 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TableOfContents } from '@/components/TableOfContents';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTranslation } from 'react-i18next';

type LegalSectionType = 'terms' | 'privacy' | 'disclaimer' | 'dmca' | 'resources' | 'about' | 'security' | 'contact';

interface LegalSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  id: LegalSectionType;
  activeSection: LegalSectionType;
  onSectionChange: (section: LegalSectionType) => void;
}

// Individual legal section component
export const LegalSection = ({ 
  title, 
  icon, 
  children, 
  id, 
  activeSection, 
  onSectionChange 
}: LegalSectionProps) => {
  const isActive = activeSection === id;
  
  return (
    <div 
      id={id}
      className={`border border-[#1e2035] rounded-xl overflow-hidden mb-6 transition-all duration-300 ${
        isActive ? 'shadow-lg shadow-[#14F195]/5' : 'opacity-80'
      }`}
    >
      <Button
        variant="ghost"
        className={`w-full flex justify-between items-center p-4 text-white hover:bg-[#14141E] transition-all ${
          isActive ? 'bg-[#0c0c15] border-b border-[#1e2035]' : 'bg-[#0a0a12]'
        }`}
        onClick={() => onSectionChange(id)}
      >
        <div className="flex items-center gap-2 text-left">
          <div className={`
            p-1.5 rounded-lg ${isActive ? 'bg-[#14F195]/10 text-[#14F195]' : 'bg-[#1e2035]/50 text-gray-400'}
          `}>
            {icon}
          </div>
          <span className={`font-semibold ${isActive ? 'text-white' : 'text-gray-300'}`}>{title}</span>
        </div>
        {isActive ? <ChevronUp className="h-5 w-5 text-[#14F195]" /> : <ChevronDown className="h-5 w-5" />}
      </Button>
      
      <div
        className={`bg-[#0a0a12] text-gray-300 text-sm relative ${
          isActive ? '' : 'hidden'
        }`}
      >
        {/* Scrollable content area */}
        <ScrollArea className="h-[min(70vh,800px)] rounded-b-xl border-t border-[#1e2035]/50">
          <div className="p-6 space-y-4">
            {children}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

// Floating section navigation component
const SectionNav = ({ 
  activeSection, 
  onSectionChange 
}: { 
  activeSection: LegalSectionType, 
  onSectionChange: (section: LegalSectionType) => void 
}) => {
  const { t } = useTranslation();
  
  return (
    <div className="bg-[#0c0c15]/90 backdrop-blur-md border border-[#1e2035] rounded-xl p-4 sticky top-24 mb-6 shadow-lg">
      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
        <BookOpen className="h-4 w-4 text-[#14F195]" />
        Legal Documents
      </h3>
      
      <nav className="space-y-2">
        <Button 
          variant="ghost" 
          className={`w-full justify-start text-sm p-2 h-auto ${
            activeSection === 'about' ? 'bg-[#14F195]/10 text-[#14F195]' : 'text-gray-300'
          }`}
          onClick={() => onSectionChange('about')}
        >
          <HelpCircle className="h-4 w-4 mr-2" />
          {t('legalsections.information')}
          {activeSection === 'about' && <ChevronRight className="h-4 w-4 ml-auto" />}
        </Button>

        <Button 
          variant="ghost" 
          className={`w-full justify-start text-sm p-2 h-auto ${
            activeSection === 'terms' ? 'bg-[#14F195]/10 text-[#14F195]' : 'text-gray-300'
          }`}
          onClick={() => onSectionChange('terms')}
        >
          <FileText className="h-4 w-4 mr-2" />
          {t('legalsections.terms_of_service')}
          {activeSection === 'terms' && <ChevronRight className="h-4 w-4 ml-auto" />}
        </Button>
        
        <Button 
          variant="ghost" 
          className={`w-full justify-start text-sm p-2 h-auto ${
            activeSection === 'privacy' ? 'bg-[#14F195]/10 text-[#14F195]' : 'text-gray-300'
          }`}
          onClick={() => onSectionChange('privacy')}
        >
          <ShieldCheck className="h-4 w-4 mr-2" />
          {t('legalsections.privacy_policy')}
          {activeSection === 'privacy' && <ChevronRight className="h-4 w-4 ml-auto" />}
        </Button>
        
        <Button 
          variant="ghost" 
          className={`w-full justify-start text-sm p-2 h-auto ${
            activeSection === 'disclaimer' ? 'bg-[#14F195]/10 text-[#14F195]' : 'text-gray-300'
          }`}
          onClick={() => onSectionChange('disclaimer')}
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          {t('legalsections.disclaimer')}
          {activeSection === 'disclaimer' && <ChevronRight className="h-4 w-4 ml-auto" />}
        </Button>
        
        <Button 
          variant="ghost" 
          className={`w-full justify-start text-sm p-2 h-auto ${
            activeSection === 'dmca' ? 'bg-[#14F195]/10 text-[#14F195]' : 'text-gray-300'
          }`}
          onClick={() => onSectionChange('dmca')}
        >
          <Edit3 className="h-4 w-4 mr-2" />
          {t('legalsections.dmca_policy')}
          {activeSection === 'dmca' && <ChevronRight className="h-4 w-4 ml-auto" />}
        </Button>
        
        <Button 
          variant="ghost" 
          className={`w-full justify-start text-sm p-2 h-auto ${
            activeSection === 'resources' ? 'bg-[#14F195]/10 text-[#14F195]' : 'text-gray-300'
          }`}
          onClick={() => onSectionChange('resources')}
        >
          <BookOpen className="h-4 w-4 mr-2" />
          {t('legalsections.educational_resources')}
          {activeSection === 'resources' && <ChevronRight className="h-4 w-4 ml-auto" />}
        </Button>
        
        <Button 
          variant="ghost" 
          className={`w-full justify-start text-sm p-2 h-auto ${
            activeSection === 'security' ? 'bg-[#14F195]/10 text-[#14F195]' : 'text-gray-300'
          }`}
          onClick={() => onSectionChange('security')}
        >
          <ShieldCheck className="h-4 w-4 mr-2" />
          {t('legalsections.security_statement')}
          {activeSection === 'security' && <ChevronRight className="h-4 w-4 ml-auto" />}
        </Button>
      </nav>
      
      <Separator className="my-3 bg-[#1e2035]" />
      
      <div className="text-xs text-gray-400">
        <p className="flex items-center gap-1">
          <HelpCircle className="h-3 w-3" />
          Solana Volume Bot Official Documentation
        </p>
      </div>
    </div>
  );
};

// Full legal sections container with all legal content
export const LegalSections = ({ initialSection = 'terms', isStandalone = false }: { initialSection?: LegalSectionType, isStandalone?: boolean }) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<LegalSectionType>(initialSection);
  
  // URL hash handling
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'terms' || hash === 'privacy' || hash === 'disclaimer' || 
          hash === 'dmca' || hash === 'resources' || hash === 'about' || 
          hash === 'security' || hash === 'contact') {
        setActiveSection(hash as LegalSectionType);
      }
    };
    
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  
  const handleSectionChange = (section: LegalSectionType) => {
    setActiveSection(section);
    if (isStandalone) {
      window.history.pushState(null, '', `#${section}`);
    }
  };
  
  return (
    <section id="legal" className={`py-12 ${isStandalone ? 'pt-32' : ''} bg-[#080810]`}>
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto">
          {/* Left sidebar with section navigation */}
          <div className="lg:w-1/4">
            <SectionNav activeSection={activeSection} onSectionChange={handleSectionChange} />
          </div>
          
          {/* Main content section */}
          <div className="lg:w-3/4">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  Legal <span className="text-[#14F195]">{t('legalsections.information')}</span>
                </h2>
                
                {isStandalone && (
                  <Button 
                    variant="outline" 
                    className="border-[#1e2035] hover:border-[#14F195]/40 text-white"
                    onClick={() => window.history.back()}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Home
                  </Button>
                )}
              </div>
              
              <p className="text-gray-400">
                The following information outlines the legal framework for using the Solana Volume Bot service. 
                Please read these documents carefully to understand your rights and responsibilities.
              </p>
              
              <LegalSection 
                title={t('legalsections.information')} 
                icon={<HelpCircle className="h-5 w-5" />}
                id="about"
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
              >
                <div className="space-y-6">
                  <div className="p-4 bg-[#0c0c15] rounded-lg border border-[#1e2035]">
                    <p className="text-white font-medium mb-2">
                      Solana Volume Bot provides professional volume distribution services and visibility enhancement solutions for Solana-based tokens.
                    </p>
                  </div>
                
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.our_mission')}</h3>
                  <p>
                    Our mission is to provide effective volume distribution services that help Solana token creators enhance their token's visibility and optimize discoverability on major decentralized exchanges through ethical and compliant methods.
                  </p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.what_we_do')}</h3>
                  <p>
                    Solana Volume Bot offers strategic volume distribution services that help token creators achieve greater visibility on major DEX platforms. Our platform provides:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>{t('legalsections.tailored_volume_distribution_str')}</li>
                    <li>{t('legalsections.multi_wallet_transaction_coordin')}</li>
                    <li>{t('legalsections.natural_trading_pattern_simulati')}</li>
                    <li>{t('legalsections.dex_ranking_algorithm_optimizati')}</li>
                    <li>{t('legalsections.educational_resources_for_sustai')}</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.our_approach')}</h3>
                  <p>
                    We believe in transparency and ethical practices in the cryptocurrency space. Our platform focuses on providing effective volume distribution strategies through compliant methods that enhance token visibility. We do not:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>{t('legalsections.provide_financial_advice_or_inve')}</li>
                    <li>{t('legalsections.guarantee_specific_price_movemen')}</li>
                    <li>{t('legalsections.engage_in_market_manipulation_or')}</li>
                    <li>Offer "pump and dump" schemes or artificial price inflation services</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.our_team')}</h3>
                  <p>
                    Our team consists of experienced blockchain developers, DEX optimization specialists, and volume distribution experts with deep expertise in the Solana ecosystem. We're committed to providing reliable volume enhancement services that help token creators navigate the complex world of DEX visibility.
                  </p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.company_information')}</h3>
                  <p>
                    Solana Volume Bot is operated by FULL VOLUME CO. UK. LTD, a company registered in England and Wales.
                  </p>
                  <div className="mt-2">
                    <ul className="list-none space-y-1 text-gray-300">
                      <li><strong>{t('legalsections.company_number_')}</strong> 05551974</li>
                      <li><strong>{t('legalsections.registered_office_')}</strong> {t('legalsections.182_staniforth_road_sheffield_s9')}</li>
                      <li><strong>{t('legalsections.phone_')}</strong> +44 (0) 114 244 8891</li>
                      <li><strong>{t('legalsections.email_')}</strong> {t('legalsections.info_solanavolumebot_io')}</li>
                    </ul>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white">{t('footer_legal_contact')}</h3>
                  <p>
                    We welcome your questions, feedback, and inquiries about our services. Please feel free to contact us through any of the following channels:
                  </p>
                  <div className="mt-2">
                    <ul className="list-none space-y-1 text-gray-300">
                      <li><strong>{t('legalsections.email_')}</strong> {t('footer_contact_support')}</li>
                      <li><strong>{t('legalsections.phone_')}</strong> +44 (0) 114 244 8891</li>
                      <li><strong>{t('legalsections.hours_')}</strong> Monday to Friday, 9:00 AM - 5:00 PM (GMT)</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-[#0c0c15] rounded-lg border border-[#14F195]/20 mt-6">
                    <p className="text-[#14F195] font-medium mb-2">{t('legalsections.our_commitment_to_accessibility')}</p>
                    <p className="text-gray-300 text-sm">
                      Solana Volume Bot is committed to making our website and services accessible to everyone. We follow WCAG 3.0 guidelines to ensure our platform can be easily navigated and used by people with diverse abilities and needs. If you encounter any accessibility issues or have suggestions for improvement, please contact our support team.
                    </p>
                  </div>
                </div>
              </LegalSection>
              
              <LegalSection 
                title={t('legalsections.terms_of_service')} 
                icon={<FileText className="h-5 w-5" />}
                id="terms"
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
              >
                <div className="space-y-6">
                  <div className="p-4 bg-[#0c0c15] rounded-lg border border-[#1e2035]">
                    <p className="text-sm text-amber-300 mb-2">
                      <strong>{t('legalsections.important_')}</strong> Solana Volume Bot is a technical visibility optimization tool for Solana-based tokens. 
                      It is not a financial product and does not guarantee trading profits, investment returns, or specific price outcomes.
                    </p>
                    <p className="text-sm text-gray-400">
                      <strong>{t('legalsections.company_information_')}</strong> {t('legalsections.full_volume_co_uk_ltd')}<br />
                      Company Number: 05551974<br />
                      Registered in England and Wales<br />
                      Registered Office: 182 Staniforth Road, Sheffield, S9 3HF, United Kingdom<br />
                      Phone: +44 (0) 114 244 8891
                    </p>
                  </div>
                
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.1_acceptance_of_terms')}</h3>
                  <p>By accessing and using the Solana Volume Bot service ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.2_description_of_service')}</h3>
                  <p>The Solana Volume Bot provides technical visibility enhancement for Solana-based tokens. Our service helps increase your token's presence on DEX platforms including DEXTools and Pump.fun through strategic and optimized transaction patterns, intended solely to improve discoverability - not to manipulate prices or markets.</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.3_account_registration_and_secur')}</h3>
                  <p>{t('legalsections.to_use_the_sol_volume_bot_you_mu')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.4_service_usage_guidelines')}</h3>
                  <p>{t('legalsections.you_agree_to_use_the_pump_fun_vo')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.5_subscription_and_payments')}</h3>
                  <p>{t('legalsections.our_service_is_offered_through_s')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.6_modifications_to_service')}</h3>
                  <p>{t('legalsections.we_reserve_the_right_to_modify_s')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.7_limitation_of_liability')}</h3>
                  <p>The Solana Volume Bot is provided "as is" and "as available" without any warranties, express or implied. We do not guarantee that the Service will be uninterrupted, secure, or error-free. We shall not be liable for any direct, indirect, incidental, special, or consequential damages.</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.8_indemnification')}</h3>
                  <p>{t('legalsections.you_agree_to_indemnify_and_hold_')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.9_governing_law')}</h3>
                  <p>{t('legalsections.these_terms_shall_be_governed_by')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.10_termination')}</h3>
                  <p>{t('legalsections.we_may_terminate_or_suspend_your')}</p>
                </div>
              </LegalSection>
              
              <LegalSection 
                title={t('legalsections.privacy_policy')} 
                icon={<ShieldCheck className="h-5 w-5" />}
                id="privacy"
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
              >
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.1_information_collection')}</h3>
                  <p>{t('legalsections.the_solana_volume_bot_collects_i')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.2_usage_information')}</h3>
                  <p>{t('legalsections.when_you_use_our_service_we_auto')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.3_use_of_information')}</h3>
                  <p>{t('legalsections.we_use_the_information_we_collec')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.4_information_sharing')}</h3>
                  <p>{t('legalsections.the_dextools_volume_bot_does_not')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.5_cookies_and_similar_technologi')}</h3>
                  <p>{t('legalsections.we_use_cookies_and_similar_track')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.6_data_security')}</h3>
                  <p>{t('legalsections.we_implement_appropriate_securit')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.7_data_retention')}</h3>
                  <p>{t('legalsections.we_retain_personal_information_c')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.8_your_rights')}</h3>
                  <p>{t('legalsections.depending_on_your_location_you_m')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.9_changes_to_privacy_policy')}</h3>
                  <p>{t('legalsections.we_may_update_our_privacy_policy')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.10_contact_information')}</h3>
                  <p>{t('legalsections.if_you_have_questions_about_this')}</p>
                </div>
              </LegalSection>
              
              <LegalSection 
                title={t('legalsections.security_statement')} 
                icon={<ShieldCheck className="h-5 w-5" />}
                id="security"
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
              >
                <div className="space-y-6">
                  <div className="p-4 bg-[#0c0c15] rounded-lg border border-[#14F195]/20">
                    <p className="text-white font-medium mb-2">
                      At Solana Volume Bot, we prioritize the security and privacy of our users. This security statement outlines the measures we take to protect your data and ensure a safe experience on our platform.
                    </p>
                  </div>
                
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.our_security_approach')}</h3>
                  <p>
                    Security is fundamental to our service. We implement industry-standard security practices and continuously update our security measures to protect against emerging threats. Our approach includes:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>{t('legalsections.regular_security_audits_and_vuln')}</li>
                    <li>{t('legalsections.secure_coding_practices_and_code')}</li>
                    <li>{t('legalsections.encryption_of_sensitive_data_bot')}</li>
                    <li>{t('legalsections.strict_access_controls_and_authe')}</li>
                    <li>{t('legalsections.regular_security_training_for_ou')}</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.data_protection')}</h3>
                  <p>
                    We take data protection seriously and have implemented the following measures:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>{t('legalsections.encryption_of_personal_and_finan')}</li>
                    <li>{t('legalsections.secure_database_configurations_w')}</li>
                    <li>{t('legalsections.regular_data_backups_with_secure')}</li>
                    <li>{t('legalsections.data_minimization_principles_we_')}</li>
                    <li>{t('legalsections.scheduled_data_deletion_for_info')}</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.no_private_key_requirements')}</h3>
                  <p>
                    Our service is designed to operate without requiring access to your private keys. We never ask for, store, or have access to your cryptocurrency wallet private keys. This design principle ensures that:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>{t('legalsections.your_crypto_assets_remain_comple')}</li>
                    <li>{t('legalsections.there_is_no_risk_of_unauthorized')}</li>
                    <li>{t('legalsections.your_wallet_security_is_maintain')}</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.wallet_connection_security')}</h3>
                  <p>
                    When you choose to connect a wallet to our service, we implement the following security measures:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>{t('legalsections.read_only_connections_that_preve')}</li>
                    <li>{t('legalsections.secure_connection_protocols_with')}</li>
                    <li>{t('legalsections.clear_permission_screens_showing')}</li>
                    <li>{t('legalsections.option_to_disconnect_your_wallet')}</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.authentication_authorization')}</h3>
                  <p>
                    We use secure authentication methods to protect your account:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>{t('legalsections.strong_password_requirements_wit')}</li>
                    <li>{t('legalsections.email_verification_for_account_c')}</li>
                    <li>{t('legalsections.session_timeout_and_automatic_lo')}</li>
                    <li>{t('legalsections.ip_monitoring_to_detect_suspicio')}</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.incident_response')}</h3>
                  <p>
                    We have a comprehensive incident response plan to address security incidents promptly:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>{t('legalsections.dedicated_security_team_for_moni')}</li>
                    <li>{t('legalsections.defined_procedures_for_containin')}</li>
                    <li>{t('legalsections.transparent_communication_with_a')}</li>
                    <li>{t('legalsections.continuous_improvement_of_securi')}</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.security_best_practices_for_user')}</h3>
                  <p>
                    We recommend the following security practices for all users:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>{t('legalsections.use_unique_strong_passwords_for_')}</li>
                    <li>Keep your email account secure as it's used for account recovery</li>
                    <li>{t('legalsections.be_vigilant_against_phishing_att')}</li>
                    <li>{t('legalsections.keep_your_operating_system_and_b')}</li>
                    <li>{t('legalsections.log_out_of_your_account_when_usi')}</li>
                  </ul>
                  
                  <div className="p-4 bg-[#0c0c15] rounded-lg border border-amber-500/30 mt-6">
                    <p className="text-amber-400 font-medium mb-2">{t('legalsections.reporting_security_issues')}</p>
                    <p className="text-gray-300 text-sm">
                      If you discover a security vulnerability or have concerns about the security of our platform, please contact us immediately at security@solanavolumebot.io. We take all security reports seriously and will investigate promptly.
                    </p>
                  </div>
                </div>
              </LegalSection>
              
              <LegalSection 
                title={t('legalsections.disclaimer')} 
                icon={<AlertTriangle className="h-5 w-5" />}
                id="disclaimer"
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
              >
                <div className="space-y-6">
                  <div className="p-4 bg-[#0c0c15] rounded-lg border border-amber-700/30">
                    <h3 className="font-semibold text-amber-300 mb-2">{t('legalsections.important_risk_disclosure')}</h3>
                    <p className="text-amber-100/80 text-sm">
                      The information on this website is for informational and educational purposes only.
                      It is not intended to be and does not constitute financial advice or any other advice.
                    </p>
                  </div>
                
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.1_not_financial_advice')}</h3>
                  <p>{t('legalsections.the_solana_volume_bot_is_a_techn')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.2_no_investment_recommendations')}</h3>
                  <p>{t('legalsections.we_do_not_make_recommendations_r')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.3_high_risk_activity')}</h3>
                  <p>{t('legalsections.cryptocurrency_trading_and_token')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.4_no_guarantees_of_outcomes')}</h3>
                  <p>{t('legalsections.we_do_not_guarantee_any_specific')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.5_technical_limitations')}</h3>
                  <p>{t('legalsections.our_service_operates_within_the_')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.6_regulatory_compliance')}</h3>
                  <p>{t('legalsections.users_are_solely_responsible_for')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.7_third_party_services')}</h3>
                  <p>{t('legalsections.our_service_interacts_with_third')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.8_no_professional_relationship')}</h3>
                  <p>{t('legalsections.use_of_our_service_does_not_crea')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.9_independent_verification')}</h3>
                  <p>{t('legalsections.users_should_independently_verif')}</p>
                </div>
              </LegalSection>
              
              <LegalSection 
                title={t('legalsections.dmca_policy')} 
                icon={<Edit3 className="h-5 w-5" />}
                id="dmca"
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
              >
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.1_digital_millennium_copyright_a')}</h3>
                  <p>The Solana Volume Bot respects the intellectual property rights of others and expects its users to do the same. In accordance with the Digital Millennium Copyright Act (DMCA), we will respond to notices of alleged copyright infringement.</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.2_dmca_notification_requirements')}</h3>
                  <p>{t('legalsections.to_file_a_copyright_infringement')}</p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-300">
                    <li>{t('legalsections.a_physical_or_electronic_signatu')}</li>
                    <li>{t('legalsections.identification_of_the_copyrighte')}</li>
                    <li>{t('legalsections.identification_of_the_material_t')}</li>
                    <li>{t('legalsections.information_reasonably_sufficien')}</li>
                    <li>{t('legalsections.a_statement_that_you_have_a_good')}</li>
                    <li>{t('legalsections.a_statement_that_the_information')}</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.3_where_to_send_dmca_notices')}</h3>
                  <p>{t('legalsections.dmca_notices_concerning_our_serv')}</p>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.4_counter_notification')}</h3>
                  <p>{t('legalsections.if_material_that_you_have_posted')}</p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-300">
                    <li>{t('legalsections.your_physical_or_electronic_sign')}</li>
                    <li>{t('legalsections.identification_of_the_material_t')}</li>
                    <li>{t('legalsections.a_statement_under_penalty_of_per')}</li>
                    <li>{t('legalsections.your_name_address_and_telephone_')}</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-white">{t('legalsections.5_policy_against_repeat_infringe')}</h3>
                  <p>In appropriate circumstances, the service will disable and/or terminate the accounts of users who are repeat infringers of the copyrights of others. We consider a "repeat infringer" to be any user who has uploaded content to our service and for whom we have received more than two takedown notices.</p>
                </div>
              </LegalSection>
              
              <LegalSection 
                title={t('legalsections.educational_resources')} 
                icon={<BookOpen className="h-5 w-5" />}
                id="resources"
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
              >
                <div className="space-y-6">
                  <div className="p-4 bg-[#0c0c15] rounded-lg border border-[#14F195]/20">
                    <p className="text-sm text-[#14F195] mb-2">
                      <strong>{t('legalsections.learning_resources_')}</strong> The following educational materials are provided to help you understand token visibility strategies, analytics, and security best practices.
                    </p>
                  </div>
                
                  {/* Token Visibility Guide */}
                  <div className="p-5 bg-[#0A0A12] rounded-lg border border-[#1e2035] hover:border-[#14F195]/30 transition-all">
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <FileText className="text-[#14F195] h-5 w-5" />
                      Token Visibility Guide
                    </h3>
                    
                    <div className="space-y-4">
                      <p>Understanding how tokens gain visibility on decentralized exchanges (DEXs) is essential for project success. This guide covers the fundamentals of token visibility.</p>
                      
                      <h4 className="text-md font-medium text-[#14F195]">{t('legalsections.key_topics_')}</h4>
                      <ul className="list-disc pl-6 space-y-1 text-gray-300">
                        <li>{t('legalsections.understanding_dex_listing_factor')}</li>
                        <li>{t('legalsections.volume_indicators_and_their_sign')}</li>
                        <li>{t('legalsections.organic_growth_vs_artificial_pat')}</li>
                        <li>{t('legalsections.creating_sustainable_visibility_')}</li>
                        <li>{t('legalsections.analyzing_successful_token_visib')}</li>
                      </ul>
                      
                      <p className="text-sm text-gray-400 italic">
                        Our comprehensive guidelines help you navigate token visibility challenges while maintaining ethical practices and regulatory compliance.
                      </p>
                    </div>
                  </div>
                  
                  {/* Volume Optimization Guide */}
                  <div className="p-5 bg-[#0A0A12] rounded-lg border border-[#1e2035] hover:border-[#14F195]/30 transition-all">
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <FileText className="text-[#9945FF] h-5 w-5" />
                      Volume Optimization Guide
                    </h3>
                    
                    <div className="space-y-4">
                      <p>{t('legalsections.understanding_how_to_optimize_vo')}</p>
                      
                      <h4 className="text-md font-medium text-[#9945FF]">{t('legalsections.key_topics_')}</h4>
                      <ul className="list-disc pl-6 space-y-1 text-gray-300">
                        <li>{t('legalsections.optimizing_volume_distribution_p')}</li>
                        <li>{t('legalsections.understanding_dex_ranking_algori')}</li>
                        <li>{t('legalsections.implementing_sustainable_visibil')}</li>
                        <li>{t('legalsections.balancing_transaction_frequencie')}</li>
                        <li>{t('legalsections.recognizing_effective_vs_ineffic')}</li>
                      </ul>
                      
                      <p className="text-sm text-gray-400 italic">
                        This educational guide helps you understand visibility optimization without making investment recommendations.
                      </p>
                    </div>
                  </div>
                  
                  {/* Security Best Practices */}
                  <div className="p-5 bg-[#0A0A12] rounded-lg border border-[#1e2035] hover:border-[#14F195]/30 transition-all">
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <ShieldCheck className="text-[#03E1FF] h-5 w-5" />
                      Security Best Practices
                    </h3>
                    
                    <div className="space-y-4">
                      <p>{t('legalsections.security_is_paramount_in_the_blo')}</p>
                      
                      <h4 className="text-md font-medium text-[#03E1FF]">{t('legalsections.key_topics_')}</h4>
                      <ul className="list-disc pl-6 space-y-1 text-gray-300">
                        <li>{t('legalsections.wallet_security_best_practices')}</li>
                        <li>{t('legalsections.smart_contract_audit_importance')}</li>
                        <li>{t('legalsections.recognizing_phishing_and_scam_at')}</li>
                        <li>{t('legalsections.api_key_management_and_security')}</li>
                        <li>{t('legalsections.multi_signature_protection_strat')}</li>
                      </ul>
                      
                      <p className="text-sm text-gray-400 italic">
                        These educational materials are regularly updated to reflect current security standards and emerging threats in the blockchain ecosystem.
                      </p>
                    </div>
                  </div>
                  
                  {/* Contact for More Resources */}
                  <div className="p-4 bg-[#0c0c15] rounded-lg border border-[#1e2035] mt-8">
                    <p className="text-center text-gray-400">
                      Need more specialized resources? <button onClick={() => handleSectionChange('contact')} className="text-[#14F195] hover:underline">{t('legalsections.contact_our_team')}</button> for personalized educational materials.
                    </p>
                  </div>
                </div>
              </LegalSection>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};